/**
 * Any table or series to CSV (RFC 4180): the columns a screen shows, with raw values (seconds,
 * shares, ISO days) rather than the words on screen, so a spreadsheet can sum them. The same
 * column list draws the table view of a chart, so the file always holds what the screen shows.
 *
 * `toCsv` and `csvName` are pure; `downloadCsv` hands the text to the browser.
 */

export type Cell = string | number | null;

export interface Column<T> {
  /** The column heading on screen. */
  label: string;
  /** The heading in the file when it differs (a unit: "Watch time (seconds)"). */
  csvLabel?: string;
  value: (row: T) => Cell;
  /** How the value reads on screen; the file keeps the raw value. */
  format?: (value: Cell, row: T) => string;
  /** Right-aligned in the table. */
  numeric?: boolean;
  /** The metric's meaning, shown beside the heading on screen (definitions.ts). */
  tooltip?: string;
  /** In the file only (an id a spreadsheet can join on; the screen shows the name). */
  csvOnly?: boolean;
  /** On screen the cell links here (an item's page); the file keeps the value. */
  href?: (row: T) => string | undefined;
}

/** A table: what both the table view and the file are built from. */
export interface Table<T> {
  columns: ReadonlyArray<Column<T>>;
  rows: readonly T[];
}

function field(value: Cell): string {
  if (value === null) return "";
  const text = typeof value === "number" ? (Number.isFinite(value) ? String(value) : "") : value;
  return /[",\r\n]/.test(text) || /^\s|\s$/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv<T>({ columns, rows }: Table<T>): string {
  const lines = [columns.map((c) => field(c.csvLabel ?? c.label)), ...rows.map((row) => columns.map((c) => field(c.value(row))))];
  return lines.map((line) => line.join(",")).join("\r\n") + "\r\n";
}

/** "northlight-content-2026-10-01-to-2026-10-28.csv". */
export function csvName(brand: string, what: string, range?: { from: string; to: string }): string {
  const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return `${[slug(brand), slug(what), ...(range ? [range.from, "to", range.to] : [])].filter(Boolean).join("-")}.csv`;
}

/** Saves the text as a file (a byte order mark first, so spreadsheets read it as UTF-8). */
export function downloadCsv(name: string, text: string): void {
  const url = URL.createObjectURL(new Blob(["﻿", text], { type: "text/csv;charset=utf-8" }));
  const link = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
